import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useProductsServices,
  useCreateProductService,
  useUpdateProductService,
  useApproveProductService,
  useRejectProductService,
  useDeactivateProductService,
  ProductService,
} from "@/hooks/useProductsServices";
import { useProductCategories, ProductCategory } from "@/hooks/useProductCategories";
import { useChartOfAccounts, ChartOfAccount } from "@/hooks/useChartOfAccounts";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, ShieldCheck, ShieldX, AlertTriangle, Eye } from "lucide-react";

const CATEGORY_ICONS: Record<string, string> = {
  labor: "👷", material: "🧱", equipment: "🏗", subcontract: "🔄", other: "📋",
};

const STATUS_STYLES: Record<string, string> = {
  active: "bg-primary/15 text-primary border-primary/30",
  pending_approval: "bg-yellow-50 text-yellow-700 border-yellow-200",
  draft: "bg-muted text-muted-foreground border-border",
  inactive: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const categoryBadge = (cat: string) => {
  const colors: Record<string, string> = {
    labor: "bg-blue-50 text-blue-700 border-blue-200",
    material: "bg-amber-50 text-amber-700 border-amber-200",
    equipment: "bg-purple-50 text-purple-700 border-purple-200",
    subcontract: "bg-green-50 text-green-700 border-green-200",
    other: "bg-muted text-muted-foreground border-border",
  };
  return colors[cat] || colors.other;
};

export const ProductsServicesTab = () => {
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: products = [], isLoading } = useProductsServices(catFilter, statusFilter);
  const { data: categories = [] } = useProductCategories();
  const { data: accounts = [] } = useChartOfAccounts();
  const { data: roleInfo } = useUserRole();
  const isController = roleInfo?.roleName === "controller" || roleInfo?.roleName === "admin";

  const createProduct = useCreateProductService();
  const updateProduct = useUpdateProductService();
  const approveProduct = useApproveProductService();
  const rejectProduct = useRejectProductService();
  const deactivateProduct = useDeactivateProductService();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProductService | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null);

  // Approval/Reject dialog
  const [reviewProduct, setReviewProduct] = useState<ProductService | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("SF");
  const [costPrice, setCostPrice] = useState("0");
  const [markupPct, setMarkupPct] = useState("35");
  const [incomeAccount, setIncomeAccount] = useState("");
  const [cogsAccount, setCogsAccount] = useState("");
  const [approverNotes, setApproverNotes] = useState("");

  const pendingCount = products.filter((p) => p.status === "pending_approval").length;

  const filteredIncomeAccounts = (cat: ProductCategory | null) => {
    if (!cat) return accounts;
    const min = parseInt(cat.income_account_min);
    const max = parseInt(cat.income_account_max);
    return accounts.filter((a) => {
      const num = parseInt(a.account_number);
      return num >= min && num <= max;
    });
  };

  const filteredCogsAccounts = (cat: ProductCategory | null) => {
    if (!cat) return accounts;
    const min = parseInt(cat.cogs_account_min);
    const max = parseInt(cat.cogs_account_max);
    return accounts.filter((a) => {
      const num = parseInt(a.account_number);
      return num >= min && num <= max;
    });
  };

  const openNew = () => {
    setEditing(null);
    setStep(1);
    setSelectedCategory(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (p: ProductService) => {
    setEditing(p);
    const cat = categories.find((c) => c.id === p.category_id) || null;
    setSelectedCategory(cat);
    setName(p.name);
    setDescription(p.description || "");
    setUnit(p.unit);
    setCostPrice(String(p.default_cost_price ?? 0));
    setMarkupPct(String(p.default_markup_pct ?? 35));
    setIncomeAccount(p.income_account || "");
    setCogsAccount(p.cogs_account || "");
    setApproverNotes("");
    setStep(2);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setName(""); setDescription(""); setUnit("SF"); setCostPrice("0");
    setMarkupPct("35"); setIncomeAccount(""); setCogsAccount(""); setApproverNotes("");
  };

  const selectCategory = (cat: ProductCategory) => {
    setSelectedCategory(cat);
    setMarkupPct(String(cat.default_tax_code === "TAX" ? 20 : 35));
    // Pre-select first valid accounts
    const incAccts = filteredIncomeAccounts(cat);
    const cogsAccts = filteredCogsAccounts(cat);
    if (incAccts.length > 0) setIncomeAccount(incAccts[0].account_number);
    if (cogsAccts.length > 0) setCogsAccount(cogsAccts[0].account_number);
    setUnit(cat.allowed_units[0] || "SF");
    setStep(2);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!selectedCategory) { toast.error("Category is required"); return; }
    if (!incomeAccount || !cogsAccount) { toast.error("Account mapping required"); return; }

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const payload: Record<string, any> = {
        company_id: companyId,
        name: name.trim(),
        description: description || null,
        category: selectedCategory.code,
        category_id: selectedCategory.id,
        unit,
        default_cost_price: parseFloat(costPrice) || 0,
        default_markup_pct: parseFloat(markupPct) || 0,
        income_account: incomeAccount,
        cogs_account: cogsAccount,
        is_taxable: selectedCategory.default_tax_code === "TAX",
        qb_tax_code: selectedCategory.default_tax_code,
        is_active: true,
      };

      if (editing) {
        await updateProduct.mutateAsync({ id: editing.id, ...payload });
        toast.success("Product updated");
      } else {
        payload.status = isController ? "active" : "pending_approval";
        payload.code = ""; // Auto-generated by trigger
        await createProduct.mutateAsync(payload);
        toast.success(isController ? "Product created & activated" : "Submitted for approval");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save");
    }
  };

  const handleApprove = async (p: ProductService) => {
    try {
      await approveProduct.mutateAsync(p.id);
      toast.success(`${p.name} approved`);
      setReviewProduct(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleReject = async () => {
    if (!reviewProduct || !rejectReason.trim()) { toast.error("Reason required"); return; }
    try {
      await rejectProduct.mutateAsync({ id: reviewProduct.id, reason: rejectReason });
      toast.success(`${reviewProduct.name} rejected`);
      setReviewProduct(null);
      setShowReject(false);
      setRejectReason("");
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDeactivate = async (p: ProductService) => {
    if (p.has_transactions) {
      toast.error("Cannot deactivate — has existing transactions. It will remain on historical records.");
      return;
    }
    try {
      await deactivateProduct.mutateAsync(p.id);
      toast.success("Product deactivated");
    } catch (err: any) { toast.error(err.message); }
  };

  const accountLabel = (num: string | null) => {
    if (!num) return "—";
    const acct = accounts.find((a) => a.account_number === num);
    return acct ? `${acct.account_number} · ${acct.name}` : num;
  };

  return (
    <div className="space-y-4">
      {/* Pending approval banner */}
      {isController && pendingCount > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <span className="font-medium">{pendingCount} product{pendingCount > 1 ? "s" : ""} pending your approval.</span>
            <Button variant="link" className="ml-2 p-0 h-auto text-yellow-800 underline" onClick={() => setStatusFilter("pending_approval")}>
              Review Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Products & Services</h2>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending_approval">Pending</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> {isController ? "New Product" : "Request New"}</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Markup %</TableHead>
                <TableHead>Income Acct</TableHead>
                <TableHead>COGS Acct</TableHead>
                <TableHead className="text-center">Tax</TableHead>
                <TableHead className="text-center">QB</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></TableCell></TableRow>
              ) : products.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No products found</TableCell></TableRow>
              ) : products.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">{p.code}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={categoryBadge(p.category)}>
                      {CATEGORY_ICONS[p.category] || "📦"} {p.category}
                    </Badge>
                  </TableCell>
                  <TableCell>{p.unit}</TableCell>
                  <TableCell className="text-right">{p.default_markup_pct}%</TableCell>
                  <TableCell className="font-mono text-xs">{accountLabel(p.income_account)}</TableCell>
                  <TableCell className="font-mono text-xs">{accountLabel(p.cogs_account)}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={p.is_taxable ? "bg-destructive/10 text-destructive border-destructive/30" : "text-muted-foreground"}>
                      {p.qb_tax_code}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={
                      p.qb_synced ? "bg-primary/15 text-primary border-primary/30"
                        : p.qb_item_id ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : "text-muted-foreground"
                    }>
                      {p.qb_synced ? "Synced" : p.qb_item_id ? "Pending" : "—"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[p.status] || ""}>{p.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isController && p.status === "pending_approval" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={() => setReviewProduct(p)} title="Review">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isController && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isController && p.status === "active" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeactivate(p)} title="Deactivate">
                          <ShieldX className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product/Service" : step === 1 ? "Select Category" : "New Product/Service"}</DialogTitle>
            <DialogDescription>
              {step === 1 ? "Choose a category to set tax treatment and account ranges." : (editing ? "Update product details." : "Fill in product details. Code is auto-generated.")}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && !editing ? (
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className="border rounded-lg p-4 text-left hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                  onClick={() => selectCategory(cat)}
                >
                  <div className="text-2xl mb-1">{CATEGORY_ICONS[cat.code] || "📦"}</div>
                  <div className="font-medium text-sm">{cat.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {cat.default_tax_code === "TAX" ? "TAXABLE" : "NON-TAXABLE"}
                  </div>
                  <div className="text-xs text-muted-foreground">{cat.description}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Category indicator */}
              {selectedCategory && (
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className={categoryBadge(selectedCategory.code)}>
                    {CATEGORY_ICONS[selectedCategory.code]} {selectedCategory.name}
                  </Badge>
                  <Badge variant="outline" className={selectedCategory.default_tax_code === "TAX" ? "bg-destructive/10 text-destructive border-destructive/30" : "text-muted-foreground"}>
                    {selectedCategory.default_tax_code === "TAX" ? "🔴 TAX — Taxable" : "🟢 NON — Non-taxable"}
                  </Badge>
                  {!editing && <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setStep(1)}>Change</Button>}
                </div>
              )}

              {/* Code - auto-generated */}
              {!editing && (
                <div>
                  <Label className="text-muted-foreground">Code</Label>
                  <Input disabled value={`${selectedCategory?.auto_code_prefix || "---"}-### — Auto-assigned`} className="bg-muted" />
                </div>
              )}

              <div>
                <Label>Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. LVP Flooring Install" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it includes, when to use it" rows={2} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Unit</Label>
                  <Select value={unit} onValueChange={setUnit}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(selectedCategory?.allowed_units || ["SF","LF","EA","LS","HR","DAY"]).map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cost Price</Label>
                  <Input type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} />
                </div>
                <div>
                  <Label>Markup %</Label>
                  <Input type="number" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Income Account</Label>
                  <Select value={incomeAccount} onValueChange={setIncomeAccount}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {filteredIncomeAccounts(selectedCategory).map((a) => (
                        <SelectItem key={a.account_number} value={a.account_number}>
                          {a.account_number} · {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Revenue from this service</p>
                </div>
                <div>
                  <Label>COGS Account</Label>
                  <Select value={cogsAccount} onValueChange={setCogsAccount}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {filteredCogsAccounts(selectedCategory).map((a) => (
                        <SelectItem key={a.account_number} value={a.account_number}>
                          {a.account_number} · {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Cost to deliver this service</p>
                </div>
              </div>

              {!isController && !editing && (
                <>
                  <div>
                    <Label>Notes for Approver</Label>
                    <Textarea value={approverNotes} onChange={(e) => setApproverNotes(e.target.value)} placeholder="Why is this product needed?" rows={2} />
                  </div>
                  <Alert className="border-yellow-200 bg-yellow-50">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800 text-xs">
                      New products require Controller approval before use. You'll be notified when approved.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={createProduct.isPending || updateProduct.isPending}>
                {(createProduct.isPending || updateProduct.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {editing ? "Update" : (isController ? "Create & Activate" : "Submit for Approval")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Review/Approval Dialog */}
      <Dialog open={!!reviewProduct} onOpenChange={(open) => { if (!open) { setReviewProduct(null); setShowReject(false); setRejectReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Product</DialogTitle>
            <DialogDescription>Approve or reject this product request.</DialogDescription>
          </DialogHeader>
          {reviewProduct && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Code:</span> <span className="font-mono">{reviewProduct.code}</span></div>
                <div><span className="text-muted-foreground">Category:</span> <Badge variant="outline" className={categoryBadge(reviewProduct.category)}>{reviewProduct.category}</Badge></div>
                <div className="col-span-2"><span className="text-muted-foreground">Name:</span> <span className="font-medium">{reviewProduct.name}</span></div>
                {reviewProduct.description && <div className="col-span-2"><span className="text-muted-foreground">Description:</span> {reviewProduct.description}</div>}
                <div><span className="text-muted-foreground">Unit:</span> {reviewProduct.unit}</div>
                <div><span className="text-muted-foreground">Markup:</span> {reviewProduct.default_markup_pct}%</div>
                <div><span className="text-muted-foreground">Income:</span> <span className="font-mono text-xs">{accountLabel(reviewProduct.income_account)}</span></div>
                <div><span className="text-muted-foreground">COGS:</span> <span className="font-mono text-xs">{accountLabel(reviewProduct.cogs_account)}</span></div>
                <div><span className="text-muted-foreground">Tax:</span> <Badge variant="outline" className={reviewProduct.is_taxable ? "bg-destructive/10 text-destructive" : ""}>{reviewProduct.qb_tax_code}</Badge></div>
              </div>

              {showReject ? (
                <div className="space-y-2">
                  <Label>Rejection Reason *</Label>
                  <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain why this product is being rejected..." rows={3} />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setShowReject(false)}>Cancel</Button>
                    <Button variant="destructive" size="sm" onClick={handleReject} disabled={rejectProduct.isPending}>
                      {rejectProduct.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Confirm Reject
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 justify-end">
                  <Button variant="destructive" size="sm" onClick={() => setShowReject(true)}>
                    <ShieldX className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button size="sm" onClick={() => handleApprove(reviewProduct)} disabled={approveProduct.isPending}>
                    {approveProduct.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
